import type { Option } from "@tento-lona/sheets";
import { RowKey } from "@tento-lona/sheets";
import { isCanonicalKey } from "@tento-lona/sheets/platform";

/**
 * The one key rule for cell access: which string keys a row's cell
 * store (RowCellCache registry entry, accessor cache slot, chokepoint
 * routing).
 *
 * Canonical provider rows (per `isCanonicalKey`: bare `:~rest` like
 * `:~lona:tasks`, or provider-namespaced `[provider]:~rest` like
 * `[weather@lona.so]:~weather:{id}`) key by the canonical lookup key
 * regardless of how the row is backed — virtual, lookup-form, or
 * server UUID. Multiple sheets and standalone `rows.open()` clients
 * then converge on ONE cell store per canonical key, so local edits
 * made through any surface are visible to every reader. The same
 * predicate routes rows-side reads (`SourceRow.peekCells` →
 * `canonicalCells`), keeping both worlds on one rule. Everything
 * else keys by its own row id.
 *
 * Every handle-resolution site MUST use this rule
 * (`cellAccessRowKey` in sheet-tree.ts, `_ensureRowHandle` in
 * sheet-row.ts, `CellAccessor._accessKeyFor`,
 * `SheetDataSource.applyCellEditLocal`). Divergent keying is the
 * root cause of the "edit doesn't render until refresh" bug class —
 * a write lands in one store while the renderer reads another.
 */
export function cellAccessKey(
  rowIdString: string,
  lookupKey: Option<string>,
): string {
  if (!lookupKey) return rowIdString;
  const key = RowKey.of(rowIdString);
  if (key.isVirtual() || key.isLookup() || isCanonicalKey(lookupKey)) {
    return lookupKey;
  }
  return rowIdString;
}
